'use client'

import gsap from 'gsap'
import { useLayoutEffect } from 'react'
import Tempus from 'tempus'
import { ScrollTriggerConfig } from './scroll-trigger'

export function GSAP({ scrollTrigger = false }) {
  // Set up GSAP performance optimization
  useLayoutEffect(() => {
    // Set better defaults for performance
    gsap.defaults({
      ease: 'none',
      overwrite: 'auto', // Prevents conflicting animations
      lazy: true, // Improves performance by only evaluating when needed
    })

    // Configure ticker for better performance
    gsap.ticker.lagSmoothing(0)
    gsap.ticker.remove(gsap.updateRoot)

    // Optimize for low performance devices if needed
    if (detectLowPerformanceDevice()) {
      // Lower GSAP CPU/GPU usage
      gsap.ticker.fps(30) // Reduce update rate for better performance
      gsap.globalTimeline.timeScale(0.8) // Slightly slow down animations
    } else {
      gsap.ticker.fps(60) // Use standard frame rate on high-performance devices
    }

    // Sync with Tempus for better performance
    Tempus?.add((time: number) => {
      gsap.updateRoot(time / 1000)
    })

    // Check for page visibility to pause animations when tab is not visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        gsap.globalTimeline.pause()
      } else {
        gsap.globalTimeline.resume()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  return scrollTrigger && <ScrollTriggerConfig />
}

// Detect low performance devices to automatically optimize
function detectLowPerformanceDevice(): boolean {
  // Skip in SSR
  if (typeof window === 'undefined') return false

  // Check for mobile devices
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )

  // Check for low memory devices
  const hasLowMemory = !!(
    // @ts-ignore - Non-standard but useful API
    (navigator.deviceMemory && navigator.deviceMemory < 4)
  )

  // Check hardware concurrency (processor cores)
  const hasLowCPU = !!(navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4)

  // Run quick performance test
  const startTime = performance.now()
  for (let i = 0; i < 10000; i++) {
    Math.sqrt(Math.random() * 10000)
  }
  const endTime = performance.now()
  const isSlowCPU = endTime - startTime > 10 // More than 10ms suggests a slower CPU

  return (isMobile && (hasLowMemory || hasLowCPU)) || isSlowCPU
}
